import React, { useEffect } from "react";
import moment from "moment";

import { z } from "zod";
import { NumericFormat } from "react-number-format";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { getFileRequest } from "../../../../helpers/request";
import { getExtension, truncateMiddle } from "../../../../helpers/helpers";
import {
  clearAll,
  showDocumentViewerModalFunc,
} from "../../../../config/redux/slice/bankUserSlice";
import {
  setErrorMessageBankUser,
  setLoadingBankUser,
} from "../../../../config/redux/slice/bankUserSlice";
import { Spinner } from "../../../components";
import { pagesText } from "../../../../constants/english";
import {
  DATE_FORMAT_TEMPLATE,
  CERTIFICATE_STATUS,
} from "../../../../constants";
import {
  getAdditionalDocuments,
  sendDisapprovalMail,
  uploadBankCertificate,
} from "../../../../config/redux/actions/bank";

import SuccessModal from "./SuccessModal";
import { convertDateTime } from "../../../../utils/lib";

const schema = z
  .object({
    amount_requested: z.number(),
    amount_available: z
      .number({
        required_error: "*this field is required",
        invalid_type_error: "Please enter a valid value",
      })
      .positive(),
    certificate_number: z.string(),
    certificate_number_two: z.string(),
    certificate_date: z.string().date(),
    currency: z.string(),
    cover_letter: z.instanceof(File, "*this field is required"),
    expiry_date: z.string().date(),
    effective_date: z.coerce.date(),
    wht_rate_percentage: z
      .number({
        required_error: "*this field is required",
        invalid_type_error: "Please enter a valid WHT rate",
      })
      .min(1, "Rate percentage must be at least 1%")
      .max(100, "Rate percentage cannot be greater than 100%"),
  })
  .refine(
    (data) =>
      parseFloat(data.amount_available) <= parseFloat(data.amount_requested),
    {
      message: "Amount available cannot be greater than amount requested",
      path: ["amount_available"],
    },
  );

function CertificateOfReasonablenessUploadForm({ certificateObject }) {
  const dispatch = useDispatch();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      amount_requested: parseInt(certificateObject?.AMOUNT_REQUEST),
      wht_rate_percentage: "",
      certificate_number: certificateObject?.CERTIFICATE_NUMBER,
      certificate_number_two: certificateObject?.CERTIFICATE_NUMBER_TWO,
      certificate_date: certificateObject?.CERTIFICATE_DATE,
      currency: certificateObject?.CURRENCY,
      expiry_date: certificateObject?.EXPIRY_DATE,
      effective_date: certificateObject?.EFFECTIVE_DATE,
    },
  });

  const {
    bankUserLoading,
    bankUserSuccessMsg,
    certificateManagement: { otp, reference_number, additionalDocs },
  } = useSelector((state) => state.bankUser);

  const { token } = useSelector((state) => state.authData);

  useEffect(() => {
    if (certificateObject.ID)
      dispatch(getAdditionalDocuments(certificateObject.ID));
  }, [certificateObject.ID, dispatch]);

  const onSubmit = (data) => {
    const formData = new FormData();

    formData.append("amount_available", data.amount_available);
    formData.append("wht_rate_percentage", data.wht_rate_percentage);
    formData.append("otp", otp);
    formData.append("reference", reference_number);
    formData.append("cover_letter", data.cover_letter);

    dispatch(clearAll({}));
    dispatch(uploadBankCertificate(formData));
  };

  const onSendEmail = () => {
    dispatch(clearAll({}));
    dispatch(sendDisapprovalMail());
  };

  const downloadFiles = async () => {
    dispatch(clearAll({}));

    let created_at = convertDateTime(
      certificateObject?.CREATED_AT,
      pagesText.full_date_for_download,
    );

    let additionalFiles = additionalDocs.map((document) => ({
      url: document.FILE_URL,
      name: `${document.FILE_NAME}_${certificateObject?.CERTIFICATE_NUMBER}_${created_at}`,
      ext: getExtension(document.FILE_URL),
    }));

    let urls = [
      {
        url: certificateObject?.CERTIFICATE_FILE_PATH,
        name: `certificate_${certificateObject?.CERTIFICATE_NUMBER}_${created_at}`,
        ext: getExtension(certificateObject?.CERTIFICATE_FILE_PATH),
      },
      {
        url: certificateObject?.STAMPED_AGREEMENT,
        name: `stamped_agreement_${certificateObject?.CERTIFICATE_NUMBER}_${created_at}`,
        ext: getExtension(certificateObject?.STAMPED_AGREEMENT),
      },
      {
        url: certificateObject?.COVER_LETTER,
        name: `cover_letter_${certificateObject?.CERTIFICATE_NUMBER}_${created_at}`,
        ext: getExtension(certificateObject?.COVER_LETTER),
      },
      ...additionalFiles,
    ];

    await getFileRequest(
      token,
      dispatch,
      setLoadingBankUser,
      setErrorMessageBankUser,
      `Application_Documents_${created_at}`,
      urls,
    );
  };

  return (
    <div
      className="modal fade show"
      id="basicModal"
      tabIndex="-1"
      aria-labelledby="exampleModalCenterTitle"
      aria-hidden="true"
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,0.95)",
        overflowY: "auto",
      }}
    >
      <div
        style={{ overflowY: "auto" }}
        className={`modal-dialog modal-dialog-centered ${bankUserSuccessMsg ? "modal-md" : "modal-lg"}`}
        role="document"
      >
        <div className="modal-content">
          {bankUserSuccessMsg ? (
            <SuccessModal message={bankUserSuccessMsg} />
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              autoComplete={"off"}
              noValidate
            >
              <div className="modal-header px-4">
                <h5 className="modal-title" id="exampleModalCenterTitle">
                  {certificateObject?.CERTIFICATE_STATUS ===
                  CERTIFICATE_STATUS.DISAPPROVED
                    ? "Certificate Rejection"
                    : "Confirm Reasonableness"}
                </h5>
              </div>

              <div className="modal-body px-4 px-md-7">
                {certificateObject?.CERTIFICATE_STATUS ===
                  CERTIFICATE_STATUS.DISAPPROVED && (
                  <div className="mb-3">
                    <h6
                      style={{ lineHeight: 1.6 }}
                      className="mb-2 text-danger"
                    >
                      This certificate has been rejected by the National Office
                      For Technology Acquisition and Promotion (NOTAP). Please
                      click the button below to send an email to the transferee
                      informing them to update all the missing and incorrect
                      information
                    </h6>

                    {bankUserLoading ? (
                      <Spinner />
                    ) : (
                      <button
                        onClick={onSendEmail}
                        type="button"
                        className="btn btn-primary"
                      >
                        Send Email
                      </button>
                    )}
                  </div>
                )}

                <div>
                  <h6 className="mb-3 font-weight-bold">
                    Certificate Information
                  </h6>
                  <div className="row mb-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="certificate_number">
                          Reference ID (File No)
                        </label>
                        <input
                          type="text"
                          defaultValue={certificateObject?.CERTIFICATE_NUMBER}
                          className="form-control input-lg"
                          id="certificate_number"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="certificate_number_two">
                          Certificate Number
                        </label>
                        <input
                          type="text"
                          defaultValue={
                            certificateObject?.CERTIFICATE_NUMBER_TWO
                          }
                          className="form-control input-lg"
                          id="certificate_number_two"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="certificate_date">
                          Certificate Issue Date
                        </label>
                        <input
                          type="date"
                          defaultValue={moment(
                            certificateObject?.CERTIFICATE_DATE,
                          ).format(DATE_FORMAT_TEMPLATE)}
                          max={moment().format(DATE_FORMAT_TEMPLATE)}
                          className="form-control input-lg"
                          id="certificate_date"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="expiry_date">
                          Certificate Expiry Date
                        </label>
                        <input
                          type="date"
                          defaultValue={moment(
                            certificateObject?.EXPIRY_DATE,
                          ).format(DATE_FORMAT_TEMPLATE)}
                          max={moment().format(DATE_FORMAT_TEMPLATE)}
                          className="form-control input-lg"
                          id="expiry_date"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="effective_date">
                          Certificate Effective Date
                        </label>
                        <input
                          type="date"
                          defaultValue={moment(
                            certificateObject?.EFFECTIVE_DATE,
                          ).format(DATE_FORMAT_TEMPLATE)}
                          max={moment().format(DATE_FORMAT_TEMPLATE)}
                          className="form-control input-lg"
                          id="effective_date"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="currency">Currency</label>
                        <input
                          type="text"
                          defaultValue={certificateObject?.CURRENCY}
                          className="form-control input-lg"
                          id="currency"
                          disabled
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="amount_requested">
                          Amount Requested
                        </label>

                        <NumericFormat
                          allowLeadingZeros={false}
                          className="form-control input-lg"
                          thousandSeparator=","
                          decimalSeparator="."
                          disabled
                          defaultValue={parseFloat(
                            certificateObject?.AMOUNT_REQUEST,
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    style={{ columnGap: "0.8rem" }}
                    className="d-flex align-items-center mb-3"
                  >
                    <h6 className="font-weight-bold">Document Upload</h6>

                    <button
                      type="button"
                      title="Download files"
                      className="btn btn-sm btn-primary"
                      onClick={downloadFiles}
                    >
                      <i className="mdi mdi-arrow-down mr-1"></i>
                      <span>Download files</span>
                    </button>
                  </div>

                  <div className="row mb-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="certificate_file">Certificate</label>

                        <div>
                          <div
                            onClick={() =>
                              dispatch(
                                showDocumentViewerModalFunc({
                                  modal: true,
                                  url: certificateObject?.CERTIFICATE_FILE_PATH,
                                }),
                              )
                            }
                            className="custom-file mb-1"
                          >
                            <div className="custom-file-input"></div>
                            <div
                              id="custom-certificate"
                              className="custom-file-label disabled"
                            >
                              Certificate
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="stamped_agreement">
                          Stamped Agreement
                        </label>

                        <div>
                          <div
                            onClick={() =>
                              dispatch(
                                showDocumentViewerModalFunc({
                                  modal: true,
                                  url: certificateObject?.STAMPED_AGREEMENT,
                                }),
                              )
                            }
                            className="custom-file mb-1"
                          >
                            <div className="custom-file-input"></div>

                            <div
                              id="custom-certificate2"
                              className="custom-file-label disabled"
                            >
                              Stamped Agreement
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="cover_letter">Cover Letter</label>

                        <div>
                          <div
                            onClick={() =>
                              dispatch(
                                showDocumentViewerModalFunc({
                                  modal: true,
                                  url: certificateObject?.COVER_LETTER,
                                }),
                              )
                            }
                            className="custom-file mb-1"
                          >
                            <div className="custom-file-input"></div>
                            <div
                              id="custom-certificate3"
                              className="custom-file-label disabled"
                            >
                              Cover Letter
                            </div>
                          </div>
                        </div>
                      </div>
                    </div> */}
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="cover_letter">Cover Letter</label>
                        <div>
                          <div className="custom-file mb-1">
                            <Controller
                              control={control}
                              name="cover_letter"
                              render={(props) => (
                                <>
                                  <input
                                    type="file"
                                    className="custom-file-input"
                                    id="cover_letter"
                                    onChange={(event) =>
                                      props.field.onChange(
                                        event.target.files?.[0],
                                      )
                                    }
                                  />

                                  <label
                                    id="custom-certificate"
                                    className="custom-file-label"
                                  >
                                    {props.field.value?.name
                                      ? truncateMiddle(
                                          props.field.value.name,
                                          30,
                                        )
                                      : "Choose file..."}
                                  </label>
                                </>
                              )}
                            />
                            {errors.cover_letter && (
                              <div className="text-danger small mt-1">
                                {errors.cover_letter.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {additionalDocs.map((document) => (
                      <div key={document.ID} className="col-lg-6">
                        <div className="form-group">
                          <label htmlFor={document.FILE_NAME}>
                            {document.FILE_NAME}
                          </label>

                          <div>
                            <div
                              onClick={() =>
                                dispatch(
                                  showDocumentViewerModalFunc({
                                    modal: true,
                                    url: document.FILE_URL,
                                  }),
                                )
                              }
                              className="custom-file mb-1"
                            >
                              <div className="custom-file-input"></div>
                              <div
                                id={`custom-certificate${document.ID + 6}`}
                                className="custom-file-label disabled"
                              >
                                {document.FILE_NAME}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h6 className="mb-3 font-weight-bold">Bank Information</h6>
                  <div className="row mb-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="amount_available">
                          Amount Available
                        </label>

                        <Controller
                          control={control}
                          name="amount_available"
                          disabled={
                            certificateObject?.CERTIFICATE_STATUS ===
                            CERTIFICATE_STATUS.DISAPPROVED
                          }
                          render={(props) => (
                            <NumericFormat
                              allowLeadingZeros={false}
                              className="form-control input-lg"
                              thousandSeparator=","
                              decimalSeparator="."
                              value={parseFloat(props.field.value)}
                              disabled={props.field.disabled}
                              onValueChange={(values) =>
                                props.field.onChange(values.floatValue)
                              }
                            />
                          )}
                        />

                        {errors.amount_available && (
                          <div className="text-danger small mt-1">
                            {errors.amount_available.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label htmlFor="wht_rate_percentage">WHT Rate</label>

                        <input
                          defaultValue={certificateObject?.WHT_RATE_PERCENTAGE}
                          className="form-control input-lg"
                          id="wht_rate_percentage"
                          disabled={
                            certificateObject?.CERTIFICATE_STATUS ===
                            CERTIFICATE_STATUS.DISAPPROVED
                          }
                          {...register("wht_rate_percentage", {
                            valueAsNumber: true,
                          })}
                        />

                        {errors.wht_rate_percentage && (
                          <div className="text-danger small mt-1">
                            {errors.wht_rate_percentage.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {certificateObject?.CERTIFICATE_STATUS !==
                CERTIFICATE_STATUS.DISAPPROVED && (
                <div className="modal-footer px-4 d-flex align-items-center">
                  <button
                    disabled={bankUserLoading}
                    onClick={reset}
                    type="button"
                    className="btn btn-smoke"
                    data-dismiss="modal"
                  >
                    Reset
                  </button>

                  <div>
                    {bankUserLoading ? (
                      <Spinner />
                    ) : (
                      <button
                        type="submit"
                        disabled={!isValid}
                        className="btn btn-primary"
                      >
                        Confirm Reasonableness
                      </button>
                    )}
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default CertificateOfReasonablenessUploadForm;
